import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { IMAGES } from "@/constants/assets";

const SPRING = { stiffness: 90, damping: 22, mass: 0.55 };

const HEADLINE = ["Steel", "in its most", "refined form."];

export const HeroShowcase = () => {
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end end"],
  });

  // Smooth the raw scroll progress with a spring so every derived transform inherits fluidity
  const progress = useSpring(scrollYProgress, SPRING);

  // Center (hero) image never leaves — it just shrinks into a card
  const centerW = useTransform(progress, [0, 0.5], ["100vw", "30vw"]);
  const centerH = useTransform(progress, [0, 0.5], ["100vh", "70vh"]);
  const centerRadius = useTransform(progress, [0, 0.5], [0, 12]);

  // Headline lives on the full hero, lifts away as it shrinks
  const headlineOpacity = useTransform(progress, [0, 0.16], [1, 0]);
  const headlineY = useTransform(progress, [0, 0.2], [0, -70]);

  // Readability gradient fades as image becomes a card
  const gradOpacity = useTransform(progress, [0, 0.4], [1, 0.15]);

  // Side cards enter in a zigzag so they don't overlap with the center
  const leftX = useTransform(progress, [0.12, 0.55], ["-55vw", "-30vw"]);
  const leftOpacity = useTransform(progress, [0.14, 0.44], [0, 1]);
  const leftY = useTransform(progress, [0.12, 0.55], ["8vh", "2vh"]);

  const rightX = useTransform(progress, [0.12, 0.55], ["55vw", "30vw"]);
  const rightOpacity = useTransform(progress, [0.14, 0.44], [0, 1]);
  const rightY = useTransform(progress, [0.12, 0.55], ["-8vh", "-2vh"]);

  // Card meta labels appear once composition forms
  const metaOpacity = useTransform(progress, [0.45, 0.62], [0, 1]);

  // FAR-LEFT card (top corner) — pulled far out and up so it doesn't overlap the left card
  const farLeftX = useTransform(progress, [0.22, 0.7], ["-80vw", "-40vw"]);
  const farLeftY = useTransform(progress, [0.22, 0.7], ["-40vh", "-22vh"]);
  const farLeftOpacity = useTransform(progress, [0.28, 0.6], [0, 1]);

  // FAR-RIGHT card (bottom corner) — mirror of far-left
  const farRightX = useTransform(progress, [0.22, 0.7], ["80vw", "38vw"]);
  const farRightY = useTransform(progress, [0.22, 0.7], ["40vh", "22vh"]);
  const farRightOpacity = useTransform(progress, [0.28, 0.6], [0, 1]);

  return (
    <section
      id="home"
      ref={wrapRef}
      data-testid="hero-showcase"
      className="relative h-[300vh] bg-[#f5f3ee]"
    >
      <div className="sticky top-0 h-screen min-h-[100svh] w-full overflow-hidden">
        {/* FAR-LEFT card (top corner) */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <motion.figure
            style={{ x: farLeftX, y: farLeftY, opacity: farLeftOpacity }}
            className="relative h-[28vh] w-[15vw] overflow-hidden rounded-xl border border-black/10 bg-[#f5f3ee] shadow-2xl shadow-black/30"
          >
            <img
              data-testid="hero-card-far-left-image"
              src={IMAGES[4].src}
              alt={IMAGES[4].label}
              className="h-full w-full object-contain"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          </motion.figure>
        </div>

        {/* FAR-RIGHT card (bottom corner) */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <motion.figure
            style={{ x: farRightX, y: farRightY, opacity: farRightOpacity }}
            className="relative h-[42vh] w-[17vw] overflow-hidden rounded-xl border border-black/10 bg-[#f5f3ee] shadow-2xl shadow-black/30"
          >
            <img
              data-testid="hero-card-far-right-image"
              src={IMAGES[5].src}
              alt={IMAGES[5].label}
              className="h-full w-full object-contain"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          </motion.figure>
        </div>

        {/* LEFT card (smaller) */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <motion.figure
            style={{ x: leftX, y: leftY, opacity: leftOpacity }}
            className="relative h-[42vh] w-[20vw] overflow-hidden rounded-xl border border-black/10 bg-[#f5f3ee] shadow-2xl shadow-black/30"
          >
            <img
              data-testid="hero-card-left-image"
              src={IMAGES[1].src}
              alt={IMAGES[1].label}
              className="h-full w-full object-contain"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          </motion.figure>
        </div>

        {/* RIGHT card (medium) */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <motion.figure
            style={{ x: rightX, y: rightY, opacity: rightOpacity }}
            className="relative h-[36vh] w-[20vw] overflow-hidden rounded-xl border border-black/10 bg-[#f5f3ee] shadow-2xl shadow-black/30"
          >
            <img
              src={IMAGES[2].src}
              alt={IMAGES[2].label}
              className="h-full w-full object-contain"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          </motion.figure>
        </div>

        {/* CENTER hero — shrinks but never leaves */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <motion.div
            style={{
              width: centerW,
              height: centerH,
              borderRadius: centerRadius,
            }}
            className="relative overflow-hidden bg-[#e9e5dc] shadow-2xl shadow-black/30"
          >
            <img
              src={IMAGES[0].src}
              alt={IMAGES[0].label}
              className="h-full w-full object-cover"
            />

            <motion.div
              style={{ opacity: gradOpacity }}
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30"
            />

            {/* Headline (masked line reveal) */}
            <motion.div
              style={{ opacity: headlineOpacity, y: headlineY }}
              className="absolute inset-x-0 bottom-[12vh] px-8 lg:px-16"
            >
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 1 }}
                className="font-tech mb-6 text-[11px] uppercase tracking-[0.45em] text-[#c6a87c]"
              >
                Steel Windows &nbsp;·&nbsp; Doors &nbsp;·&nbsp; Entrances
              </motion.p>
              <h1 className="font-serif-display text-[13vw] font-light leading-[0.92] text-white sm:text-[10vw] lg:text-[8.5rem]">
                {HEADLINE.map((line, i) => (
                  <span key={line} className="block overflow-hidden py-[0.05em]">
                    <motion.span
                      className="block"
                      initial={{ y: "110%" }}
                      animate={{ y: "0%" }}
                      transition={{
                        delay: 0.5 + i * 0.14,
                        duration: 1.1,
                        ease: [0.19, 1, 0.22, 1],
                      }}
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
              </h1>
            </motion.div>

            {/* Center label once framed */}
            <motion.div
              style={{ opacity: metaOpacity }}
              className="absolute bottom-4 left-4"
            >
              <p className="font-tech text-[9px] uppercase tracking-[0.3em] text-transparent">
                {" "}
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll cue — dark ink on the light hero canvas */}
        <motion.div
          style={{ opacity: headlineOpacity }}
          className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
        >
          <span className="font-tech text-[9px] uppercase tracking-[0.4em] text-black/50">
            Scroll
          </span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-8 w-px bg-black/40"
          />
        </motion.div>
      </div>
    </section>
  );
};
